import { useEffect, useState } from "react";
import { Box, MenuItem } from "@mui/material";
import { useLeaderboardActionModules, useLeaderboardModuleById } from "../../../store/selectors/serviceModulesSelector";
import { fetchLeaderboardActionModulesThunk, fetchLeaderboardModuleThunk, fetchLeaderboardTableThunk } from "../../../store/thunk/serviceModulesThunk";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { ActionModulePickerContainer, CenteredWrapper, DatePickerContainer, LeaderboardPageWrapper, StyledButton, StyledDatePicker, StyledSelect } from "./Leaderboard.styles";
import { useParams } from "react-router-dom";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { useTranslation } from "react-i18next";
import { PaginationParameters, PaginationResult } from "../../../api/Pagination";
import { useServerSidePagination } from "../../../hooks/PaginationHooks";
import { LeaderboardEntry, LeaderboardServiceModule } from "../../../types/serviceModuleTypes";
import LeaderboardTable from "./LeaderboardTable";
import dayjs from "dayjs";

const LeaderboardPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { serviceModuleId } = useParams<{ serviceModuleId: string }>();
    const serviceModuleIdNumber = Number(serviceModuleId);
    const isValidServiceModuleId = !isNaN(serviceModuleIdNumber) && serviceModuleIdNumber > 0;
    const leaderboardModule = useLeaderboardModuleById(serviceModuleId || "");
    const assignedActionModuleIds = leaderboardModule?.assignedActionModuleIds || [];
    const [selectedActionModuleId, setSelectedActionModuleId] = useState<number | null>(null);
    const actionModules = useLeaderboardActionModules();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

    const breadcrumbs = [
        { title: "serviceModulesPage.serviceModules", link: "serviceModules" },
        { title: "serviceModulesPage.leaderboardPage.leaderboard", link: `serviceModules/leaderboard/${serviceModuleId}` },
    ];

    const initialPaginationParameters: PaginationParameters = {
        offset: 0,
        limit: 10,
    };

    useEffect(() => {
        if (!serviceModuleId) return;
        dispatch(fetchLeaderboardModuleThunk(Number(serviceModuleId))).then((res) => {
            if (fetchLeaderboardModuleThunk.fulfilled.match(res)) {
                const leaderboard = res.payload as LeaderboardServiceModule
                dispatch(fetchLeaderboardActionModulesThunk({
                    isCompanyWideAssigned: leaderboard.isCompanyWideAssigned,
                    groupIds: leaderboard.assignedGroupIds,
                }));
            }
        });
    }, [dispatch, serviceModuleId]);

    const paginatedData = useServerSidePagination<LeaderboardEntry>(
        async (paginationParameters: PaginationParameters): Promise<PaginationResult<LeaderboardEntry>> => {
            if (!isValidServiceModuleId || selectedActionModuleId === null) {
                return { items: [], totalCount: 0 };
            }
            const formattedDate = selectedDate?.format("YYYY-MM-DD") ?? dayjs().format("YYYY-MM-DD");

            const resultAction = await dispatch(
                fetchLeaderboardTableThunk({
                    pagination: paginationParameters,
                    serviceModuleId: serviceModuleIdNumber,
                    actionModuleId: selectedActionModuleId,
                    date: formattedDate,
                })
            );

            if (fetchLeaderboardTableThunk.fulfilled.match(resultAction)) {
                return resultAction.payload;
            } else {
                return { items: [], totalCount: 0 };
            }
        },
        isValidServiceModuleId && selectedActionModuleId !== null ? [serviceModuleIdNumber, selectedActionModuleId, selectedDate] : [],
        initialPaginationParameters.order,
        initialPaginationParameters.sort
    );

    const handleLoadLeaderboard = () => {
        paginatedData.updateTableItems();
    };

    return (
        <LeaderboardPageWrapper>
            <PageTitle breadcrumbs={breadcrumbs} title={t("serviceModulesPage.leaderboardPage.leaderboard")} />
            <CenteredWrapper>
                <ActionModulePickerContainer>
                    <StyledSelect
                        value={selectedActionModuleId ?? ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSelectedActionModuleId(val === "" ? null : Number(val));
                        }}
                        displayEmpty
                        disabled={assignedActionModuleIds.length === 0}
                    >
                        <MenuItem value="" disabled>
                            {t("serviceModulesPage.leaderboardPage.selectActionModule")}
                        </MenuItem>
                        {assignedActionModuleIds.map((id) => {
                            const module = actionModules.find((m) => m.id === id);
                            return (
                                <MenuItem key={id} value={id}>
                                    <Box>{module?.name}</Box>
                                </MenuItem>
                            );
                        })}
                    </StyledSelect>
                </ActionModulePickerContainer>

                <DatePickerContainer>
                    <StyledDatePicker
                        value={selectedDate ? dayjs(selectedDate, "DD.MM.YYYY") : null}
                        onChange={(newValue) => {
                            setSelectedDate(dayjs.isDayjs(newValue) ? newValue : null);
                        }}
                        format="DD.MM.YYYY"
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                size: "small",
                                placeholder: t("label.selectDate"),
                            },
                        }}
                    />

                    <StyledButton
                        variant="contained"
                        onClick={handleLoadLeaderboard}
                        disabled={selectedActionModuleId === null}>
                        OK
                    </StyledButton>
                </DatePickerContainer>
            </CenteredWrapper>
            {selectedActionModuleId === null ? (
                <CenteredWrapper>
                    <div>{t("serviceModulesPage.leaderboardPage.pleaseSelectActionModule")}</div>
                </CenteredWrapper>
            ) : (
                <LeaderboardTable paginatedData={paginatedData} />
            )}
        </LeaderboardPageWrapper>
    );
};

export default LeaderboardPage;
